import React, { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass, Coins } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { CategoryDrawer } from "@/components/admin/wi/CategoryDrawer";
import { Card, Empty, Pill, SYSTEMS, aud, num, selectCls } from "@/components/admin/wi/ui";
import { adminApi } from "@/lib/api";

export default function WiPricing() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [system, setSystem] = useState("");
  const [reliable, setReliable] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    adminApi.get("/admin/wi/categories", { params: { q, system, reliable } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [q, system, reliable]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="wi-pricing-page">
      <AdminHeader title="Repair Pricing Database" subtitle="Standardised repair categories with historical benchmarks (ex GST). Click a category to see and correct its repairs." />
      <div className="p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search repairs, e.g. water pump, awning arm" data-testid="wi-pricing-search"
              className={`${selectCls} w-full pl-9`} />
          </div>
          <select value={system} onChange={(e) => setSystem(e.target.value)} className={selectCls} data-testid="wi-pricing-system">
            <option value="">All systems</option>{SYSTEMS.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={reliable} onChange={(e) => setReliable(e.target.value)} className={selectCls} data-testid="wi-pricing-reliable">
            <option value="">All categories</option><option value="true">Reliable benchmarks only</option><option value="false">Insufficient data</option>
          </select>
          <span className="text-xs text-neutral-500" data-testid="wi-pricing-count">{rows ? `${num(rows.length)} categories` : ""}</span>
        </div>
        <Card title="Repair categories" icon={Coins}>
          {!rows ? <div className="h-40 animate-pulse" /> : !rows.length ? <Empty>No categories yet — run the historical import first.</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left">
                  <tr className="border-b border-neutral-800">
                    <th className="py-2 pr-3">Category</th><th className="py-2 pr-3 text-right">Comparable / lines</th><th className="py-2 pr-3 text-right">Average</th>
                    <th className="py-2 pr-3 text-right">Median</th><th className="py-2 pr-3 text-right">Min–max</th><th className="py-2 pr-3 text-right">±Tolerance</th>
                    <th className="py-2 pr-3 text-right">Labour avg</th><th className="py-2 pr-3 text-right">Parts avg</th><th className="py-2">Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} onClick={() => setOpen(c.id)} className="border-b border-neutral-900 hover:bg-white/[0.03] cursor-pointer" data-testid="wi-category-row">
                      <td className="py-2.5 pr-3"><div className="text-neutral-100">{c.name}</div><div className="text-neutral-600">{c.system}</div></td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-300">{c.n_comparable} / {c.n_lines}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-[#d9b45b] font-semibold">{aud(c.mean)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-300">{aud(c.median)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-500">{c.min !== null ? `${aud(c.min)}–${aud(c.max)}` : "—"}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-400">{c.tolerance_low ? `${aud(c.tolerance_low)}–${aud(c.tolerance_high)}` : "—"}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-400">{aud(c.labour_cost_avg)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-400">{aud(c.parts_cost_avg)}</td>
                      <td className="py-2.5">{c.reliable ? <Pill tone="green">Reliable</Pill> : <Pill tone="neutral">Insufficient data</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      {open && <CategoryDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
